import { CalcOption, ExchangeData, Totals } from "./budget";

export type ServiceData = {
  _id?: string;
  type: string;
  calcOption: CalcOption;
  supplierId: string;
  supplierName: string;
  budgetValues: ServiceValues;
  negotiationValue: ServiceValues[];
  closingValues: ServiceValues;
  versions: number[]; // Array of version numbers where this service is present. Defaults to [0] if not specified.
  /**
   * Agrupamento a que este serviço pertence ("Dia 1", "Pré-evento"…).
   *
   * É AQUI que o agrupamento separa: o mesmo par (fornecedor, categoria) pode
   * ter serviços em mais de um agrupamento — no protótipo, "A&B / GRAND HYATT"
   * tem 2 serviços no Dia 1 e 1 serviço no Dia 2. Guardar o vínculo na linha de
   * fornecedor ou na categoria não permitiria isso.
   *
   * Ausente ⇒ o serviço aparece em "Sem agrupamento" (todo orçamento gravado
   * antes desta funcionalidade).
   */
  groupId?: string;
};

export type Descriptions = {
  composition: string;
  cancelPolicy: string;
  paymentMethod: string;
  commercialConditions?: boolean;
};
export type ServiceValues = {
  serviceType: string;
  inputDate: string;
  outDate: string;
  daily: number;
  quantity: number;
  serviceApplication: string;
  observation: string;
  internalObservation?: string;
  taxData: TaxData;
  totals: Totals;
  exchangeData: ExchangeData;
  exchangeOverride?: boolean;
  descriptions: Descriptions;
  appliedTaxes: AppliedTaxes;
  appliedValues?: AppliedValues;
  /**
   * Forma de cobrança desta etapa. Ausente ⇒ usa `service.calcOption` (orçamento).
   * No fechamento/negociação permite D no orçamento e B na etapa atual.
   */
  calcOption?: CalcOption;
};

export type TaxData = {
  tariff: DefaultTax;
  serviceTax: DefaultTax;
  localTax: DefaultTax;
  tourismTax: DefaultTax;
  iof: DefaultTax;
  fee: DefaultTax;
  iss: DefaultTax;
  cbs: DefaultTax;
  ibs: DefaultTax;
  typeNfMm: CalcOption;
  overhead: DefaultTax;
  markup: DefaultTax;
  commission: DefaultTax;
  incentive: DefaultTax;
  tribute: DefaultTax;
  supplier_tribute: DefaultTax;
  doubleCharged: DefaultTax;
  extras: DefaultTax;
};

export type OverrideValue = {
  calcType: string;
  value: number;
};
export type AppliedValues = {
  markupOverride?: boolean;
  markupOverrideValue?: OverrideValue;
  commissionOverride?: boolean;
  commissionOverrideValue?: OverrideValue;
  supplierTributeOverride?: boolean;
  supplierTributeOverrideValue?: OverrideValue;
};
export type AppliedTaxes = {
  serviceTax: Boolean;
  iss: Boolean;
  cbs: Boolean;
  ibs: Boolean;
  tribute: Boolean;
  supplier_tribute: Boolean;
  localTax: Boolean;
  tourismTax: Boolean;
  fee: Boolean;
  iof: Boolean;
  markup: Boolean;
  overhead: Boolean;
  commission: Boolean;
  incentive: Boolean;
  doubleCharged: Boolean;
};
export type DefaultTax = {
  typeTax: string;
  value: number;
  currencyValue: number;
  valueBRL: number;
};

export enum typeValueOptions {
  FIX_VALUE = "Valor",
  PERCENTAGE = "%",
}
export enum issTypeValueOptions {
  PERCENTAGE_RJ = "%RJ",
  PERCENTAGE = "%",
}
export enum cortesyOptions {
  CORTESY = "Cortesia",
  FIX_VALUE = "Valor Fixo",
  DAILY = "Diária",
}

export enum typeTaxEnum {
  CORTESY = "CORTESY",
  FIX_VALUE = "FIX_VALUE",
  DAILY = "DAILY",
  INCLUDED = "INCLUDED",
  AC_CLIENT = "AC_CLIENT",
}
export enum NFMMOptions {
  A = "A - Cobrar automaticamente apenas FEE",
  B = "B - Cobrar automaticamente FEE + Imposto apenas sobre FEE",
  C = "C - Cobrar automaticamente FEE + Imposto sobre Tudo (inclusive imposto ISS)",
  D = "D - Cobrar automaticamente APENAS Imposto sobre Serviço (não cobrar FEE)",
}
export enum typeTax {
  TARIFF = "Tarifa",
  SERVICE = "Taxa de Serviço",
  ISS = "ISS",
  CBS = "CBS",
  IBS = "IBS",
  TRIBUTE = "Imposto",
  LOCAL_TAX = "Variável",
  TOURISM_TAX = "Taxa de Turismo",
  FEE = "Taxa MM",
  DOUBLE_CHARGED = "Imposto Bitributado",
  IOF = "IOF",
  MARKUP = "Markup/Over",
  SUPPLIER_TRIBUTE = "Imposto sobre Markup/Over",
  OVER = "Over",
  COMMISSION = "Comissão",
  INCENTIVE = "Incentivo",
  EXTRAS = "Previsão de Extras",
}
export enum typeTaxKeys {
  tariff = "Tarifa",
  serviceTax = "Taxa de Serviço",
  iss = "ISS",
  cbs = "CBS",
  ibs = "IBS",
  tribute = "Imposto",
  doubleCharged = "Imposto Bitributado",
  localTax = "Variável",
  tourismTax = "Taxa de Turismo",
  fee = "Taxa MM",
  iof = "IOF",
  markup = "Markup/Over",
  overhead = "Over",
  commission = "Comissão",
  incentive = "Incentivo",
  supplier_tribute = "Imposto sobre Markup/Over",
}

export function mapTypeTaxToTaxKey(type: typeTax): TaxKey | undefined {
  const typeTaxMap: Record<typeTax, TaxKey> = {
    [typeTax.TARIFF]: "tariff",
    [typeTax.SERVICE]: "serviceTax",
    [typeTax.LOCAL_TAX]: "localTax",
    [typeTax.TOURISM_TAX]: "tourismTax",
    [typeTax.FEE]: "fee",
    [typeTax.IOF]: "iof",
    [typeTax.ISS]: "tariff",
    [typeTax.CBS]: "cbs",
    [typeTax.IBS]: "ibs",
    [typeTax.MARKUP]: "markup",
    [typeTax.OVER]: "overhead",
    [typeTax.INCENTIVE]: "incentive",
    [typeTax.COMMISSION]: "commission",
    [typeTax.TRIBUTE]: "tribute",
    [typeTax.SUPPLIER_TRIBUTE]: "supplier_tribute",
    [typeTax.DOUBLE_CHARGED]: "doubleCharged",
    [typeTax.EXTRAS]: "extras",
  };

  return typeTaxMap[type];
}

export enum typeTaxKeys {
  TARIFF = "tariff",
  SERVICE = "serviceTax",
  ISS = "iss",
  LOCAL_TAX = "localTax",
  TOURISM_TAX = "tourismTax",
  FEE = "fee",
  IOF = "iof",
}
export type typeTaxKey =
  | "tariff"
  | "serviceTax"
  | "iss"
  | "localTax"
  | "tourismTax"
  | "fee"
  | "iof";

export type TaxKey = keyof TaxData;

export const serviceValues = {
  serviceType: "",
  inputDate: "", // Date should be set
  outDate: "", // Date should be set
  daily: 1,
  quantity: 1,
  serviceApplication: "", // Description or application details
  observation: "",
  appliedTaxes: {
    serviceTax: true,
    iss: true,
    cbs: true,
    ibs: true,
    tribute: true,
    supplier_tribute: true,
    localTax: true,
    tourismTax: true,
    fee: true,
    iof: true,
    markup: true,
    overhead: true,
    commission: true,
    incentive: true,
    doubleCharged: true,
  },
  taxData: {
    tariff: {
      typeTax: "DAILY",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    serviceTax: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    extras: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    localTax: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    tourismTax: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    iof: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    fee: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    typeNfMm: "A" as CalcOption,
    overhead: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    markup: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    commission: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    incentive: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    iss: {
      typeTax: "PERCENTAGE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    cbs: {
      typeTax: "PERCENTAGE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    ibs: {
      typeTax: "PERCENTAGE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    tribute: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    supplier_tribute: {
      typeTax: "PERCENTAGE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
    doubleCharged: {
      typeTax: "FIX_VALUE",
      value: 0,
      currencyValue: 0,
      valueBRL: 0,
    },
  },
  totals: {
    totalInBRL: 0,
    totalInCurrency: 0,
  },
  exchangeData: {
    date: "",
    currency: "BRL",
    value: 1,
  },
  exchangeOverride: false,
  descriptions: {
    composition: "",
    cancelPolicy: "",
    paymentMethod: "",
    commercialConditions: false,
  },
};

export const initialServiceData: ServiceData = {
  _id: "", // Optional, initially empty
  type: "", // Should be set according to the actual service type
  supplierId: "", // Supplier ID, initially empty
  supplierName: "", // Supplier ID, initially empty
  budgetValues: serviceValues,
  negotiationValue: [serviceValues],
  closingValues: serviceValues,
  calcOption: "A",
  versions: [0], // Default to version 0 for backward compatibility
};
